import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from 'react-query';
import { DebouncedState, useDebouncedCallback } from 'use-debounce';
import { QueryKeys } from '~/api/queryKeys';
import { Addresses } from '~/api/scheme';
import { DEBOUNCE_DELAY } from '../constants';

type Result = {
  cities: string[];
  debouncedCitiesRefetch: DebouncedState<(city: string) => void>;
  validValue: (value: string) => boolean;
};

const useRHFCitySelect = (): Result => {
  const [cities, setCities] = useState<string[]>([]);
  const [city, setCity] = useState<string>('');
  const { getValues } = useFormContext();

  const county = getValues('address.county') || null;

  useQuery(
    QueryKeys.cities(city, county),
    () => Addresses.Cities.index({ city: city, county: county }),
    {
      onSuccess: (citiesResponse) => {
        const { data } = citiesResponse;

        if (isEmpty(data)) {
          setCities([]);
          return;
        }

        setCities(data);
      },
      onError: () => {
        setCities([]);
      },
      enabled: county?.length >= 2 || (isEmpty(county) && city?.length >= 2),
    }
  );

  useEffect(() => {
    if (city.length < 2 && isEmpty(county)) {
      setCities([]);
    }
  }, [city, county]);

  const debouncedCitiesRefetch = useDebouncedCallback((city: string) => {
    setCity(city);
  }, DEBOUNCE_DELAY);

  const validValue = (value: string): boolean => cities.includes(value);

  return {
    cities,
    debouncedCitiesRefetch,
    validValue,
  };
};

export default useRHFCitySelect;
